import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import { useLocalization } from '@experiences/locales';
import {
    SpacingToken,
    UiStack,
    UiText,
} from '@experiences/ui-common';
import { formatDate } from '@experiences/util';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import { FontVariantToken } from '@uipath/apollo-core';
import React, { useMemo } from 'react';
import type {
    ControllerRenderProps,
    FieldError,
} from 'react-hook-form';
import { useIntl } from 'react-intl';

import type {
    ByoOperationGroupConfiguration,
    LlmConnectorType,
} from '../../../../services/llm-gateway/LlmGateway.types';
import {
    ApiFlavor,
    ByoFormErrorTypes,
    ModelsConfigurationOption,
} from '../../../../services/llm-gateway/LlmGateway.types';
import {
    createEmptyLlmConfiguration,
    filterConnectorOptions,
    getConnectorDropdownValue,
    getDefaultAvailableApiFlavors,
    getDeprecatedModelsWithInfo,
    getModelConfiguration,
    getModelDescriptionTranslationKey,
    getModelIndex,
    getModelTitleTranslationKey,
    handleConnectorChange,
    translationKeyExists,
} from '../../../../services/llm-gateway/LlmGateway.utils';
import { ConnectionDropdown } from '../../subcomponents/ConnectionsDropdown';
import type { AddEditConfigurationFormData } from '../AddEditConfigurationViewModel';
import { AvailableModelsAutocomplete } from './AvailableModelsAutocomplete';
import { ConnectorDropdown } from './ConnectorDropdown';
import { LlmApiFlavorDropdown } from './LlmApiFlavorDropdown';
import { ModelComboBoxWithAdd } from './ModelComboBoxWithAdd';
import { ModelInformationSection } from './ModelInformationSection';

const useStyles = makeStyles(theme =>
    createStyles({
        modelCard: {
            border: `1px solid ${theme.palette.semantic.colorBorderDeEmp}`,
            borderRadius: '8px',
            padding: theme.spacing(2),
            backgroundColor: theme.palette.semantic.colorBackground,
            width: 'fit-content',
        },
        warningText: {
            color: theme.palette.error.main,
            marginTop: theme.spacing(0.5),
            fontSize: '12px',
        },
    }),
);

type AnyModelConfigurationSectionProps = {
    organizationId: string;
    tenantId: string;
    folderKey?: string;
    modelsConfigurationOption?: ModelsConfigurationOption;
    llmConfigurationsField: ControllerRenderProps<AddEditConfigurationFormData, 'llmConfigurations'>;
    notifyFieldChanges: () => void;
    replaceableLlms: string[];
    llmToConnectorTypeMapping: Record<string, LlmConnectorType[]>;
    vendorLlmApiFlavors?: Record<string, ApiFlavor[] | null>;
    product: string;
    feature: string;
    productConfigurations?: Record<string, Record<string, ByoOperationGroupConfiguration>>;
    error?: FieldError;
};

export const AnyModelConfigurationSection: React.FC<AnyModelConfigurationSectionProps> = ({
    organizationId,
    tenantId,
    folderKey,
    modelsConfigurationOption,
    llmConfigurationsField,
    notifyFieldChanges,
    replaceableLlms,
    llmToConnectorTypeMapping,
    vendorLlmApiFlavors,
    product,
    feature,
    productConfigurations,
    error,
}: AnyModelConfigurationSectionProps) => {
    const {
        formatMessage: translate, messages,
    } = useIntl();
    const language = useLocalization();
    const classes = useStyles();
    const llmConfigurationField = Object.values(llmConfigurationsField.value)[0];
    const currentModelValue = Object.keys(llmConfigurationsField.value)[0];

    const isAdditionMode = modelsConfigurationOption === ModelsConfigurationOption.AnyModelWithOwnAdditions;

    const isSelectedModelPredefined = useMemo(() => {
        const selectedModel = Object.keys(llmConfigurationsField.value)[0];
        return !!selectedModel && replaceableLlms.includes(selectedModel);
    }, [ llmConfigurationsField.value, replaceableLlms ]);

    const handleModelChange = (desiredModel: string, isCustomAlias: boolean) => {
        const llmConfiguration = createEmptyLlmConfiguration(desiredModel, false, isAdditionMode, isCustomAlias);

        llmConfigurationsField.onChange({ [desiredModel]: llmConfiguration });
        notifyFieldChanges();
    };

    const supportCustomConnectors = useFeatureFlagValue(Features.EnableAiTrustLayerByoLlmV2CustomConnectors.name);

    const availableApiFlavors = useMemo(() => {
        const currentModel = Object.keys(llmConfigurationsField.value)[0];
        return getDefaultAvailableApiFlavors(vendorLlmApiFlavors, productConfigurations, product, feature, currentModel, supportCustomConnectors);
    }, [ llmConfigurationsField.value, vendorLlmApiFlavors, productConfigurations, product, feature, supportCustomConnectors ]);

    const modelOverLapError = useMemo(() => {
        // No error can apply to like-for-like replacements
        if (!llmConfigurationField?.isCustomAlias) {
            return undefined;
        }

        // Only handle ModelNameOverlap errors
        if (error?.type !== ByoFormErrorTypes.ModelNameOverlap) {
            return undefined;
        }

        const errorModelName = error.message?.split(',')[1];

        // Ignore error if it doesn't pertain to the current custom alias
        if (errorModelName === llmConfigurationField?.desiredModel) {
            return error;
        }

        return undefined;
    }, [ error, llmConfigurationField?.desiredModel, llmConfigurationField?.isCustomAlias ]);

    const selectedModel = Object.keys(llmConfigurationsField.value)[0];
    const modelConfig = selectedModel
        ? getModelConfiguration(productConfigurations, product, feature, selectedModel)
        : undefined;

    const modelIndex = getModelIndex(productConfigurations, product, feature, selectedModel);
    const titleKey = modelIndex >= 0 ? getModelTitleTranslationKey(product, feature, modelIndex) : undefined;
    const descriptionKey = modelIndex >= 0 ? getModelDescriptionTranslationKey(product, feature, modelIndex) : undefined;
    const showModelTitle = !!titleKey && translationKeyExists(messages, titleKey);
    const showModelDescription = !!descriptionKey && translationKeyExists(messages, descriptionKey);

    const apiFlavorProbes = useMemo(() => {
        if (modelConfig?.probes) {
            return modelConfig.probes;
        } else if (isAdditionMode) {
            const operationGroupConfig = productConfigurations?.[product]?.[feature];
            return operationGroupConfig?.probes;
        }
        return undefined;
    }, [ isAdditionMode, productConfigurations, product, feature, modelConfig ]);

    const deprecatedModelsInfo = useMemo(() =>
        getDeprecatedModelsWithInfo(productConfigurations, product, feature),
    [ productConfigurations, product, feature ]);

    const selectedModelInfo = useMemo(() => {
        if (!currentModelValue || !deprecatedModelsInfo) {
            return null;
        }
        return deprecatedModelsInfo.get(currentModelValue);
    }, [ currentModelValue, deprecatedModelsInfo ]);

    const deprecationWarning = useMemo(() => {
        if (!selectedModelInfo) {
            return null;
        }
        const formattedDate = formatDate(selectedModelInfo.usageEndDate, language);
        return translate({ id: 'CLIENT_BYO_LLM_TO_BE_RETIRED_ON' }, { date: formattedDate });
    }, [ selectedModelInfo, language, translate ]);

    const apiFlavorWarnings = useMemo(() => {
        if (!llmConfigurationField?.apiFlavor) {
            return [];
        }
        const probe = apiFlavorProbes?.find(p => p.apiFlavor === llmConfigurationField.apiFlavor);
        return probe?.warningCodes ?? [];
    }, [ llmConfigurationField?.apiFlavor, apiFlavorProbes ]);

    const apiFlavorWarningText = useMemo(() => {
        if (apiFlavorWarnings.length === 0) {
            return null;
        }
        return apiFlavorWarnings.map(code => translate({ id: code.toUpperCase() })).join('. ');
    }, [ apiFlavorWarnings, translate ]);

    return (
        <UiStack
            className={classes.modelCard}
            gap={SpacingToken.M}
            direction='column'
        >
            {(showModelTitle || showModelDescription) && (
                <ModelInformationSection
                    titleKey={titleKey}
                    descriptionKey={descriptionKey}
                />
            )}
            <UiStack
                wrap='wrap'
                gap={SpacingToken.M}
                direction='row'
                align='start'
            >
                <UiStack
                    direction='column'
                    gap={SpacingToken.XS}
                >
                    <ModelComboBoxWithAdd
                        value={currentModelValue}
                        options={replaceableLlms}
                        modelsConfigurationOption={modelsConfigurationOption}
                        onChange={handleModelChange}
                        onBlur={llmConfigurationsField.onBlur}
                        error={modelOverLapError}
                        deprecatedModelsInfo={deprecatedModelsInfo}
                    />
                    {deprecationWarning && (
                        <UiText
                            variant={FontVariantToken.fontSizeXs}
                            className={classes.warningText}
                        >
                            {deprecationWarning}
                        </UiText>
                    )}
                </UiStack>
                <UiStack
                    direction='column'
                    gap={SpacingToken.XS}
                >
                    <LlmApiFlavorDropdown
                        value={llmConfigurationField?.apiFlavor}
                        disabled={!!modelOverLapError || !Object.keys(llmConfigurationsField.value)[0]}
                        availableApiFlavors={availableApiFlavors}
                        probes={apiFlavorProbes}
                        llmConfigurationsField={llmConfigurationsField}
                        notifyFieldChanges={notifyFieldChanges}
                        desiredModel={Object.keys(llmConfigurationsField.value)[0]}
                        llmConfiguration={llmConfigurationField}
                        autoSelectFirst={isSelectedModelPredefined}
                    />
                    {apiFlavorWarningText && (
                        <UiText
                            variant={FontVariantToken.fontSizeXs}
                            className={classes.warningText}
                        >
                            {apiFlavorWarningText}
                        </UiText>
                    )}
                </UiStack>
            </UiStack>
            <UiStack
                wrap='wrap'
                gap={SpacingToken.M}
                direction='row'>
                <ConnectorDropdown
                    organizationId={organizationId}
                    tenantId={tenantId}
                    label={translate({ id: 'CLIENT_CG_CONNECTOR' })}
                    required
                    disabled={!llmConfigurationField?.apiFlavor}
                    apiFlavor={llmConfigurationField?.apiFlavor}
                    restrictToCustomAiGateway={
                        llmConfigurationField?.apiFlavor === ApiFlavor.OpenAiChatCompletions
                        && !!llmToConnectorTypeMapping[currentModelValue]
                        && !llmToConnectorTypeMapping[currentModelValue].some(
                            (ct: LlmConnectorType) => ct === 'OpenAi' || ct === 'AzureOpenAi' || ct === 'OpenAiV1Compatible'
                        )
                    }
                    supportCustomConnectors={supportCustomConnectors}
                    filter={connectorKey =>
                        filterConnectorOptions(
                            connectorKey,
                            llmToConnectorTypeMapping,
                            llmConfigurationField,
                            currentModelValue,
                            isAdditionMode
                        )}
                    value={getConnectorDropdownValue(llmConfigurationField ?? {})}
                    onChange={(value, tags) => {
                        const [ desiredModel, llmConfiguration ] = Object.entries(llmConfigurationsField.value)[0];
                        handleConnectorChange(value, llmConfiguration, tags);
                        llmConfigurationsField.onChange({ [desiredModel]: llmConfiguration });
                        notifyFieldChanges();
                    }} />
                <ConnectionDropdown
                    organizationId={organizationId}
                    tenantId={tenantId}
                    folderKey={folderKey}
                    connectorKey={getConnectorDropdownValue(llmConfigurationField ?? {})}
                    label={translate({ id: 'CLIENT_CG_CONNECTION' })}
                    required
                    disabled={!llmConfigurationField?.connectorType}
                    value={llmConfigurationField?.connectionId}
                    onChange={(value) => {
                        const [ desiredModel, llmConfiguration ] = Object.entries(llmConfigurationsField.value)[0];
                        llmConfiguration.connectionId = value.id;
                        llmConfiguration.customerModel = '';
                        llmConfigurationsField.onChange({ [desiredModel]: llmConfiguration });

                        notifyFieldChanges();
                    }} />
                <AvailableModelsAutocomplete
                    label={translate({ id: 'CLIENT_BYO_LLM_CONNECTED_MODEL_ID' })}
                    required
                    disabled={!llmConfigurationField?.connectionId}
                    organizationId={organizationId}
                    tenantId={tenantId}
                    byoLlmConfiguration={llmConfigurationField}
                    initialValue={llmConfigurationField?.customerModel ?? ''}
                    onChange={(value) => {
                        const [ desiredModel, llmConfiguration ] = Object.entries(llmConfigurationsField.value)[0];
                        llmConfiguration.customerModel = value ?? '';
                        llmConfigurationsField.onChange({ [desiredModel]: llmConfiguration });

                        notifyFieldChanges();
                    }} />
            </UiStack>
        </UiStack>
    );
};
